import {
  CoverLetters,
  CandidateReport,
  ClientProposalPD,
  PreClientProposalPD,
  ChatMessage,
  ClassifiedEmail,
  ConversationRequest,
  LetterOfAgreementRequest,
  PaymentAuthorityRequest,
  PaymentAuthorityResponse,
  LongListreportRequest,
  CandidateReportsForDeletion,
  ConsultantBio,
  JobRole,
  ConsultantBioOption,
  SimilarRolesResponse,
  FetchSimilarRolesRequest,
  TrafficLightReport,
  SimilarRole,
  UpdatedSimilarRole,
} from './models';
import { FileWithCandidateDetails } from '../types/FileWithCandidateDetails';
import { appConfig } from '../appConfig';
import { ClientProposalRelevantInformation } from '../pages/clientproposal/components/useWorkflow';

type DefaultProps = {
  abortSignal?: AbortSignal;
  accessToken?: string;
};

const API_HOST = appConfig.api.baseUrl;
export const API_URL = API_HOST.replace(/\/$/, '') + /api/;

export function makeHeaders(accessToken = '', headers?: HeadersInit): Headers {
  const _headers = new Headers(
    headers || {
      'Content-Type': 'application/json',
    },
  );
  if (accessToken) {
    _headers.append('Authorization', `Bearer ${accessToken}`);
  }
  return _headers;
}

export async function conversationApi({
  options,
  abortSignal,
  accessToken,
}: DefaultProps & {
  options: ConversationRequest;
}): Promise<Response> {
  const response = await fetch(API_URL + 'conversation/azure_byod', {
    method: 'POST',
    headers: makeHeaders(accessToken),
    body: JSON.stringify({
      messages: options.messages,
    }),
    signal: abortSignal,
  });

  return response;
}

export async function customConversationApi(
  options: ConversationRequest,
  file: File | null,
  abortSignal?: AbortSignal,
): Promise<Response> {
  const formData = new FormData();

  if (file) {
    formData.append('file', file);
  } else {
    console.warn('No file selected');
  }

  if (options.id) {
    formData.append('conversation_id', options.id);
  } else {
    console.warn('conversation_id is undefined');
  }
  formData.append('messages', JSON.stringify(options.messages));

  const response = await fetch(API_URL + 'conversation/custom', {
    method: 'POST',
    body: formData,
    signal: abortSignal,
  });

  return response;
}

export const consultantBiosApi = {
  fetchAll: async (props?: DefaultProps): Promise<ConsultantBio[]> => {
    const response = await fetch(API_URL + 'consultantbio', {
      method: 'GET',
      headers: makeHeaders(),
      signal: props?.abortSignal,
    });
    const bios = await response.json();
    return bios as ConsultantBio[];
  },

  upsertOne: async ({
    consultantBio,
    abortSignal,
  }: DefaultProps & {
    consultantBio: ConsultantBio;
  }): Promise<Response> => {
    return await fetch(API_URL + 'consultantbio', {
      method: 'PUT',
      headers: makeHeaders(),
      body: JSON.stringify(consultantBio),
      signal: abortSignal,
    });
  },

  deleteOne: async ({
    consultantBio,
    abortSignal,
  }: DefaultProps & {
    consultantBio: ConsultantBio;
  }): Promise<Response> => {
    return await fetch(API_URL + 'consultantbio', {
      method: 'DELETE',
      headers: makeHeaders(),
      body: JSON.stringify(consultantBio),
      signal: abortSignal,
    });
  },
};

export const jobRolesApi = {
  fetchAll: async (props?: DefaultProps): Promise<JobRole[]> => {
    const response = await fetch(API_URL + 'rolesmasterlist', {
      method: 'GET',
      headers: makeHeaders(),
      signal: props?.abortSignal,
    });
    const roles = await response.json();
    return roles as JobRole[];
  },

  upsertOne: async ({
    jobRole,
    abortSignal,
  }: DefaultProps & {
    jobRole: JobRole;
  }): Promise<Response> => {
    return await fetch(API_URL + 'rolesmasterlist', {
      method: 'PUT',
      headers: makeHeaders(),
      body: JSON.stringify(jobRole),
      signal: abortSignal,
    });
  },

  deleteOne: async ({
    jobRole,
    abortSignal,
  }: DefaultProps & {
    jobRole: JobRole;
  }): Promise<Response> => {
    return await fetch(API_URL + 'rolesmasterlist', {
      method: 'DELETE',
      headers: makeHeaders(),
      body: JSON.stringify(jobRole),
      signal: abortSignal,
    });
  },
};

export async function longlistreportsApi(options: LongListreportRequest, abortSignal?: AbortSignal): Promise<Response> {
  const response = await fetch(API_URL + 'longlistreports', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      messages: options.messages,
    }),
    signal: abortSignal,
  });

  return response;
}

export async function uploadCoverLetterDocuments(
  candidateName: string,
  cv: File,
  jd: File,
  { abortSignal, accessToken }: DefaultProps,
): Promise<Response> {
  const formData = new FormData();
  formData.append('CV', cv as Blob);
  formData.append('JD', jd as Blob);
  formData.append('candidateName', candidateName);

  const body = {
    method: 'POST',
    headers: makeHeaders(accessToken, {}),
    body: formData,
    signal: abortSignal,
  };
  const response = await fetch(API_URL + 'uploadcoverletterdocuments', body);

  return response;
}

export async function clientproposalsApi({
  formdata,
  abortSignal,
  accessToken,
}: DefaultProps & {
  formdata: FormData;
}): Promise<Response> {
  const body = {
    method: 'POST',
    headers: makeHeaders(accessToken, {}),
    body: formdata,
    signal: abortSignal,
  };
  const response = await fetch(API_URL + 'uploadclientproposals', body);

  return response;
}

type ApiResponse<T> = {
  data?: T;
  error?: string;
  status: number;
};

async function handleApiResponse<T>(response: Response, onUnauthorized: () => void): Promise<ApiResponse<T>> {
  if (response.status >= 400 && response.status < 500) {
    onUnauthorized();
    return {
      error: 'Unauthorized',
      status: response.status,
    };
  }

  if (response.status >= 500) {
    return {
      error: 'Server error occurred',
      status: response.status,
    };
  }

  try {
    const data = await response.json();
    return {
      data,
      status: response.status,
    };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      status: response.status,
    };
  }
}

// Remove onUnauthorized from ApiOptions since we'll use context instead
type ApiOptions = {
  abortSignal?: AbortSignal;
};

// Create a wrapper for API calls
export const createApiCall = <T, P = void>(
  apiFunction: (params: P, onUnauthorized: () => void, options?: ApiOptions) => Promise<ApiResponse<T>>,
) => {
  return async (params: P, onUnauthorized: () => void, options?: ApiOptions): Promise<ApiResponse<T>> => {
    return apiFunction(params, onUnauthorized, options);
  };
};

export const uploadCandidateReport = createApiCall<unknown, FileWithCandidateDetails>(
  async (filesWithCandidateDetails, onUnauthorized) => {
    const formdata = new FormData();
    formdata.append(`file`, filesWithCandidateDetails!.file);
    formdata.append(`jobDescription`, filesWithCandidateDetails!.jobDescription);
    formdata.append(`candidateFirstName`, filesWithCandidateDetails!.candidateFirstName);
    formdata.append(`candidateLastName`, filesWithCandidateDetails!.candidateLastName);
    formdata.append(`positionName`, filesWithCandidateDetails!.positionName);
    formdata.append(`consultantName`, filesWithCandidateDetails!.consultantName);
    formdata.append(`consultantEmail`, filesWithCandidateDetails!.consultantEmail);
    formdata.append(`consultantPhone`, filesWithCandidateDetails!.consultantPhone);
    formdata.append(`candidateTitle`, filesWithCandidateDetails!.candidateTitle);
    formdata.append(`currentRemuneration`, filesWithCandidateDetails!.currentRemuneration);
    formdata.append(`desiredRemuneration`, filesWithCandidateDetails!.desiredRemuneration);
    formdata.append(`noticePeriod`, filesWithCandidateDetails!.noticePeriod);
    formdata.append(`upcomingLeave`, filesWithCandidateDetails!.upcomingLeave);
    formdata.append(`withoutAssessmentCriteria`, filesWithCandidateDetails!.withoutAssessmentCriteria.toString());
    formdata.append(`other`, filesWithCandidateDetails!.other);
    formdata.append(`companyApplied`, filesWithCandidateDetails!.companyApplied);
    formdata.append(`candidateRecentEmployer`, filesWithCandidateDetails!.candidateRecentEmployer);
    formdata.append(`jobNumber`, filesWithCandidateDetails!.jobNumber);
    formdata.append(`location`, filesWithCandidateDetails!.location);

    // Add profile photo if it exists
    if (filesWithCandidateDetails!.profilePhoto) {
      formdata.append('profilePhoto', filesWithCandidateDetails!.profilePhoto);
    }

    const response = await fetch(API_URL + 'uploadcandidatereport', {
      method: 'POST',
      body: formdata,
    });

    return handleApiResponse(response, onUnauthorized);
  },
);

export const deleteCandidateReport = createApiCall<unknown, CandidateReportsForDeletion>(
  async (data, onUnauthorized) => {
    const response = await fetch(API_URL + 'deletecandidatereport', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    return handleApiResponse(response, onUnauthorized);
  },
);

// dont think this is used anymore
export const uploadDocuments = createApiCall<Response, FormData>(async (formdata, onUnauthorized) => {
  const response = await fetch(API_URL + 'uploaddocument', { method: 'POST', body: formdata });
  return handleApiResponse(response, onUnauthorized);
});

export const uploadTrafficLightReport = createApiCall<Response, LongListreportRequest>(async (data, onUnauthorized) => {
  const response = await fetch(API_URL + 'uploadtrafficlightreport', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleApiResponse(response, onUnauthorized);
});

export const createLongListeportContainer = createApiCall<Response, string>(async (uniqueId, onUnauthorized) => {
  const payload = JSON.stringify({ uniqueId });

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: payload,
  };

  const response = await fetch(API_URL + 'createlonglistreportcontainer', options);
  return handleApiResponse(response, onUnauthorized);
});

export const processLongListReport = createApiCall<Response, string>(async (uniqueId, onUnauthorized) => {
  const response = await fetch(API_URL + `processlonglistreport/${uniqueId}`, { method: 'POST' });
  return handleApiResponse(response, onUnauthorized);
});

// dont think this is used anymore
export const createCandidateReport = createApiCall<Response, FormData>(async (formdata, onUnauthorized) => {
  const response = await fetch(API_URL + 'uploadcandidatereport', {
    method: 'POST',
    body: formdata,
  });
  return handleApiResponse(response, onUnauthorized);
});

// dont think this is used anymore
export const uploadInterviewPd = createApiCall<Response, FormData>(async (formdata, onUnauthorized) => {
  const response = await fetch(API_URL + 'uploadinterviewpd', {
    method: 'POST',
    body: formdata,
  });
  return handleApiResponse(response, onUnauthorized);
});

// dont think this is used anymore
export const getEmailsApi = createApiCall<ClassifiedEmail[]>(async (_, onUnauthorized) => {
  const response = await fetch(API_URL + 'emails', {
    method: 'GET',
    headers: makeHeaders(),
  });
  return handleApiResponse(response, onUnauthorized);
});

//dont think this is used anymore
export const getCandidatesReportApi = createApiCall<CandidateReport[]>(async (_, onUnauthorized) => {
  const response = await fetch(API_URL + 'candidatereports', {
    method: 'GET',
    headers: makeHeaders(),
  });
  return handleApiResponse(response, onUnauthorized);
});

//dont think this is used anymore
export const getClientProposalPDApi = createApiCall<ClientProposalPD[]>(async (_, onUnauthorized) => {
  const response = await fetch(API_URL + 'clientproposalpd', {
    method: 'GET',
    headers: makeHeaders(),
  });
  return handleApiResponse(response, onUnauthorized);
});

//dont think this is used anymore
export const getPreClientProposalPDApi = createApiCall<PreClientProposalPD[]>(async (_, onUnauthorized) => {
  const response = await fetch(API_URL + 'preclientproposals', {
    method: 'GET',
    headers: makeHeaders(),
  });
  return handleApiResponse(response, onUnauthorized);
});

export const getCoverLettersApi = createApiCall<CoverLetters[]>(async (_, onUnauthorized) => {
  const response = await fetch(API_URL + 'coverletters', {
    method: 'GET',
    headers: makeHeaders(),
  });
  return handleApiResponse(response, onUnauthorized);
});

// tmp prompt, needs to be moved to the BE as an openai function prompt
const generateLetterOfAgreementPrompt = `Generate a 'Letter of Agreement' based on our Letter of Agreement Document/s in your knowledge base.
Include all the details and questions from the Letter of Agreement Document/s.
Use values/answers from the below-provided JSON data to populate the fields in the Letter of Agreement.
If an answer/field in the Letter of Agreement is not present in the JSON data leave it as-is in the output. The uncompleted fields will be filled in later manually.
The GMW party is the party that is mentioned in the Letter of Agreement, not the GMW Officer from the JSON.
Output only the Letter of Agreement text, no other information is required.

The input JSON data is as follows:
JSON_DATA_HERE
`;

// tmp, refer to customConversationApi, later when the prompt is moved to
//  the BE, this function will be replaced with _generateLetterOfAgreementApi
export async function generateLetterOfAgreementApi({
  options,
  abortSignal,
  accessToken,
}: DefaultProps & {
  options: LetterOfAgreementRequest;
}): Promise<string> {
  const content = generateLetterOfAgreementPrompt.replace('JSON_DATA_HERE', JSON.stringify(options.emailData));
  const message: ChatMessage = {
    role: 'user',
    content,
  };
  const requestBody = {
    id: crypto.randomUUID(),
    messages: [message],
  };

  const response = await customConversationApi(requestBody, null, abortSignal);
  const responseBody = await response.json();
  const choice = responseBody.choices?.pop();
  const msg = choice?.messages?.pop();
  const loa = msg?.content || '';
  return loa as string;
}

export async function _generateLetterOfAgreementApi({
  options,
  abortSignal,
  accessToken,
}: DefaultProps & {
  options: LetterOfAgreementRequest;
}): Promise<string> {
  const response = await fetch(API_URL + 'letter_of_agreement', {
    method: 'POST',
    headers: makeHeaders(accessToken),
    body: JSON.stringify({
      email_data: options.emailData,
      conversation_id: options.id,
    }),
    signal: abortSignal,
  });
  const responseBody = await response.json();
  return responseBody.text_format as string;
}

// tmp prompt, needs to be moved to the BE as an openai function prompt
const generateDeedOfReleasePrompt = `Generate a Deed of Release based on our Deed of Release Document/s in your knowledge base.
When you encounter fields in the Deed of Release that could be populated or selected, please look in the below-provided JSON data for relevant values.
If a field is missing in the JSON data, please leave it as-is in the output, the uncompleted fields will be filled in later.
The GMW party is the party that is mentioned in the Letter of Agreement, not the GMW Officer from the JSON.
Output only the Deed of Release text, no other information is required.

The input JSON data is as follows:
JSON_DATA_HERE
`;

// tmp, refer to customConversationApi, later when the prompt is moved to
//  the BE, this function will be replaced
export async function generateDeedOfReleaseApi({
  options,
  abortSignal,
  accessToken,
}: DefaultProps & {
  options: LetterOfAgreementRequest;
}): Promise<string> {
  const content = generateDeedOfReleasePrompt.replace('JSON_DATA_HERE', JSON.stringify(options.emailData));
  const message: ChatMessage = {
    role: 'user',
    content,
  };
  const requestBody = {
    id: crypto.randomUUID(),
    messages: [message],
  };

  const response = await customConversationApi(requestBody, null, abortSignal);
  const responseBody = await response.json();
  const choice = responseBody.choices?.pop();
  const msg = choice?.messages?.pop();
  const dor = msg?.content || '';
  return dor as string;
}

//dont think this is used anymore
export const generatePaymentAuthority = createApiCall<PaymentAuthorityRequest, PaymentAuthorityResponse>(
  async (request, onUnauthorized, options) => {
    const response = await fetch(API_URL + 'payment_authority', {
      method: 'POST',
      headers: makeHeaders(),
      body: JSON.stringify(request),
      signal: options?.abortSignal,
    });
    return handleApiResponse(response, onUnauthorized);
  },
);

export const submitClientProposal = createApiCall<any, { formData: FormData }>(
  async ({ formData }, onUnauthorized, options) => {
    const response = await fetch(API_URL + 'uploadclientproposals', {
      method: 'POST',
      body: formData,
      signal: options?.abortSignal,
    });
    return handleApiResponse(response, onUnauthorized);
  },
);

export const fetchCandidateReports = createApiCall<CandidateReport[]>(async (_, onUnauthorized) => {
  const defaultOrderBy = localStorage.getItem('defaultOrderBy');
  const response = await fetch(API_URL + 'candidatereports', {
    method: 'POST',
    headers: makeHeaders(),
    body: JSON.stringify({ orderBy: defaultOrderBy }),
  });
  return handleApiResponse(response, onUnauthorized);
});

export const fetchTrafficLightReports = createApiCall<TrafficLightReport[]>(async (_, onUnauthorized) => {
  const response = await fetch(API_URL + 'longlistreports', {
    method: 'GET',
    headers: makeHeaders(),
  });
  return handleApiResponse(response, onUnauthorized);
});

export const deleteTrafficLightReport = createApiCall<any, any>(async (data, onUnauthorized) => {
  const response = await fetch(API_URL + 'deletetrafficlightreport', {
    method: 'POST',
    headers: makeHeaders(),
    body: JSON.stringify(data),
  });
  return handleApiResponse(response, onUnauthorized);
});

export const deleteCoverLetters = createApiCall<any, any>(async (data, onUnauthorized) => {
  const response = await fetch(API_URL + 'deletecoverletters', {
    method: 'POST',
    headers: makeHeaders(),
    body: JSON.stringify(data),
  });
  return handleApiResponse(response, onUnauthorized);
});

export const getConsultantBioOptions = async (): Promise<ConsultantBioOption[]> => {
  const response = await fetch(`${appConfig.api.baseUrl}/api/consultantbiosimplified`);
  const data = await response.json();
  return data.map((bio: any) => ({
    key: bio.id,
    text: bio.name,
  }));
};

export const deletePreClientProposals = createApiCall<unknown, { ids: string[] }>(async ({ ids }, onUnauthorized) => {
  const response = await fetch(`${API_URL}preclientproposals/batch`, {
    method: 'DELETE',
    headers: makeHeaders(),
    body: JSON.stringify({ ids }),
  });
  return handleApiResponse(response, onUnauthorized);
});

export const fetchSimilarRoles = createApiCall<SimilarRolesResponse, FetchSimilarRolesRequest>(
  async (data, onUnauthorized) => {
    const response = await fetch(`${API_URL}fetchsimiliarroles`, {
      method: 'POST',
      headers: makeHeaders(),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response, onUnauthorized);
  },
);

export const updateSimilarRole = createApiCall<SimilarRole, UpdatedSimilarRole>(async (data, onUnauthorized) => {
  const response = await fetch(`${API_URL}rolesmasterlist`, {
    method: 'PATCH',
    headers: makeHeaders(),
    body: JSON.stringify(data),
  });
  return handleApiResponse(response, onUnauthorized);
});

export const uploadClientProposal = createApiCall<any, { formData: FormData }>(async ({ formData }, onUnauthorized) => {
  const response = await fetch(`${API_URL}uploadclientproposals`, {
    method: 'POST',
    body: formData,
  });

  return handleApiResponse(response, onUnauthorized);
});

export const uploadClientProposalStep3 = createApiCall<
  any,
  {
    step3Data: ClientProposalRelevantInformation;
    filename?: string;
  }
>(async ({ step3Data, filename }, onUnauthorized) => {
  const formData = new FormData();
  formData.append('currentStep', '3');
  formData.append('similiarRoles', JSON.stringify(step3Data.similiarRoles));
  formData.append('similiarSectors', JSON.stringify(step3Data.similiarSectors));
  formData.append('similiarOrganisations', JSON.stringify(step3Data.similiarOrganisations));

  if (step3Data.similiarCustom) {
    formData.append('similiarCustom', JSON.stringify(step3Data.similiarCustom));
  }

  if (filename) {
    formData.append('filename', filename);
  }

  const response = await fetch(`${API_URL}uploadclientproposals`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });

  return handleApiResponse(response, onUnauthorized);
});

interface GenerateCriteriaDescriptionsResponse {
  descriptions: string[];
}

export const generateCriteriaDescriptions = createApiCall<GenerateCriteriaDescriptionsResponse, { criteria: string[] }>(
  async ({ criteria }, onUnauthorized) => {
    const response = await fetch(`${API_URL}generate-criteria-descriptions`, {
      method: 'POST',
      headers: makeHeaders(),
      credentials: 'include',
      body: JSON.stringify({ criteria }),
    });

    return handleApiResponse(response, onUnauthorized);
  },
);

export const extractCandidateNameAI = createApiCall<{ name: string }, { filename: string }>(
  async (data, onUnauthorized) => {
    const response = await fetch(`${API_URL}extractcandidatename`, {
      method: 'POST',
      headers: makeHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });

    return handleApiResponse(response, onUnauthorized);
  },
);

export const processTeamsTranscript = async (
  data: { transcript: string },
  handleAuthError?: (error: any) => void,
): Promise<ApiResponse<any>> => {
  try {
    const response = await fetch(API_URL + 'process-teams-transcript', {
      method: 'POST',
      headers: makeHeaders(),
      body: JSON.stringify(data),
    });

    return await handleApiResponse(response, () => {
      if (handleAuthError) {
        handleAuthError(new Error('Unauthorized'));
      }
    });
  } catch (error) {
    if (handleAuthError) {
      handleAuthError(error);
    }
    return { error: 'Failed to process transcript', status: 500 };
  }
};

export const processTeamsTranscriptStream = async (
  data: {
    transcript: string;
    templatePath?: string;
    candidateName?: string;
    interviewerName?: string;
    interviewDate?: string;
  },
  onStatusUpdate: (status: string, progress?: number) => void,
  onComplete: (result: any) => void,
  onError: (error: string) => void,
  handleAuthError?: (error: any) => void,
): Promise<void> => {
  try {
    const response = await fetch(API_URL + 'process-teams-transcript-stream', {
      method: 'POST',
      headers: makeHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (handleAuthError) {
          handleAuthError(new Error('Unauthorized'));
        }
        return;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventType = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // Process complete events
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEventType = line.substring(7);
        } else if (line.startsWith('data: ') && currentEventType) {
          try {
            const eventData = JSON.parse(line.substring(6));

            switch (currentEventType) {
              case 'status':
                onStatusUpdate(eventData.status, eventData.progress);
                break;
              case 'complete':
                onComplete(eventData);
                break;
              case 'error':
                onError(eventData.error || 'Unknown error occurred');
                break;
            }
          } catch (parseError) {
            console.error('Error parsing SSE data:', parseError);
          }

          // Reset current event type after processing
          currentEventType = '';
        }
      }
    }
  } catch (error) {
    console.error('Streaming error:', error);
    if (handleAuthError) {
      handleAuthError(error);
    }
    onError('Failed to process transcript');
  }
};
